// Web Push client helpers — registers the service worker, manages the
// browser's push subscription, and talks to /api/push/*. Designed to work
// transparently across desktop Chrome/Firefox and iOS 16.4+ standalone
// PWAs. iOS will not show the permission prompt unless the page is
// launched from the Home-Screen icon (we detect this and surface a hint).

export type PushPermissionState =
  | "unsupported"
  | "needs-pwa-install" // iOS only — Safari tab can't get push
  | "default"
  | "granted"
  | "denied";

export interface PushStatus {
  permission: PushPermissionState;
  subscribed: boolean;
  installedAsPwa: boolean;
}

type ScopedFetch = (path: string, init?: RequestInit) => Promise<Response>;

/** True when the page is running as an installed PWA (iOS Home-Screen
 *  launch, Android added-to-home, desktop install). iOS exposes this
 *  through navigator.standalone; everyone else uses the display-mode
 *  media query. */
function isStandalone(): boolean {
  // iOS Safari specific
  // @ts-expect-error — non-standard but the only iOS detection that works
  if (typeof navigator !== "undefined" && navigator.standalone) return true;
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(display-mode: standalone)").matches;
  }
  return false;
}

/** UA-sniff just enough to know whether we should warn about iOS
 *  Safari's home-screen requirement. */
function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !("MSStream" in window);
}

export function getPushStatus(subscribed = false): PushStatus {
  if (typeof window === "undefined") {
    return { permission: "unsupported", subscribed: false, installedAsPwa: false };
  }
  const installedAsPwa = isStandalone();
  // iOS Safari TABS don't expose the Notification/Push APIs at all — only an
  // installed Home-Screen PWA does. Check this BEFORE the generic
  // "Notification in window" test below, or iOS users always fall through to
  // "unsupported" and the Add-to-Home-Screen hint never renders on the very
  // device class it's written for.
  if (isIos() && !installedAsPwa) {
    return { permission: "needs-pwa-install", subscribed: false, installedAsPwa: false };
  }
  if (!("Notification" in window)) {
    return { permission: "unsupported", subscribed: false, installedAsPwa };
  }
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { permission: "unsupported", subscribed: false, installedAsPwa };
  }
  return {
    permission: Notification.permission as PushPermissionState,
    subscribed,
    installedAsPwa,
  };
}

/** Convert a base64url VAPID public key to the Uint8Array format
 *  PushManager.subscribe expects. */
function vapidKeyToUint8(b64url: string): Uint8Array {
  const padding = "=".repeat((4 - (b64url.length % 4)) % 4);
  const base64 = (b64url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Idempotent — registers /sw.js with scope `/`. Safe to call on every
 *  mount; the browser dedupes. Returns the registration or null on
 *  unsupported environments. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch (e) {
    console.warn("[push] sw register failed:", e);
    return null;
  }
}

/** Ask the browser for notification permission + create a push
 *  subscription + POST it to the server. Returns true if subscribed,
 *  false on user-denied or unsupported. */
export async function subscribeToPush(
  fetcher: ScopedFetch = fetch,
  label?: string
): Promise<boolean> {
  const reg = await registerServiceWorker();
  if (!reg) return false;

  // Permission prompt — must be a direct response to a user gesture.
  if (Notification.permission === "default") {
    const p = await Notification.requestPermission();
    if (p !== "granted") return false;
  } else if (Notification.permission !== "granted") {
    return false;
  }

  // Pull the VAPID public key from the server (one request, cached for
  // the page lifetime via the subscription itself).
  const vapidRes = await fetcher("/api/push/vapid");
  if (!vapidRes.ok) {
    console.warn("[push] /api/push/vapid failed:", vapidRes.status);
    return false;
  }
  const { publicKey } = await vapidRes.json();

  // Subscribe (or reuse an existing subscription on this device).
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidKeyToUint8(publicKey),
    });
  }

  // POST to the server. Pydantic on the server will reject anything
  // malformed; we just send the shape JSON.stringify gives us.
  const json = sub.toJSON();
  const subRes = await fetcher("/api/push/subscribe", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: json.keys,
      label: label || `${navigator.platform || "device"} · ${new Date().toLocaleDateString()}`,
    }),
  });
  return subRes.ok;
}

export async function unsubscribeFromPush(fetcher: ScopedFetch = fetch): Promise<boolean> {
  if (!("serviceWorker" in navigator)) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  try {
    await fetcher("/api/push/unsubscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ endpoint }),
    });
  } catch (_) {
    // Subscription is gone client-side regardless; server prune will
    // catch the next 410 on send.
  }
  return true;
}

/** Hook-friendly snapshot of subscription state. Reads the SW
 *  registration's current subscription presence; doesn't initiate
 *  permission requests. */
export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}
